'use client';

import SelectField from '@egalite/ui/Form/SelectField';
import Navigation from '@egalite/ui/IncluiPCD/JobSearch/Navigation';
import OpportunityCard from '@egalite/ui/IncluiPCD/OpportunityCard';
import Typography from '@egalite/ui/Typography';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import generateJobUrl from '@egalite/ui/IncluiPCD/JobSearch/generateJobUrl';

const Jobs = ({ contact }) => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const [data, setData] = useState({
		jobs: [],
		total: 0,
		pageSize: 0,
		pageMax: 0,
	});

	const setNewParam = useCallback(
		(key, value) => {
			const params = new URLSearchParams(searchParams.toString());
			if (key !== 'page') {
				params.set('page', '1');
			}
			params.set(key, value);
			router.push(`${pathname}?${params}#list`);
		},
		[pathname, router, searchParams],
	);

	const nextPage = useCallback(() => {
		const newPage = Number(searchParams.get('page') ?? 1) + 1;
		setNewParam('page', newPage);
	}, [searchParams, setNewParam]);

	const prevPage = useCallback(() => {
		const newPage = Number(searchParams.get('page') ?? 1) - 1;
		setNewParam('page', newPage);
	}, [searchParams, setNewParam]);

	useEffect(() => {
		if (contact) {
			const params = new URLSearchParams(searchParams.toString());
			params.set('contact', contact);
			fetch(`/api/v2/sponsor/jobs?${params}`, { next: { revalidate: 1 } })
				.then(res => res.json())
				.then(res => {
					setData({
						jobs: res.data.jobs,
						total: res.data.total,
						pageSize: res.data.pageSize,
						pageMax: Math.ceil(Math.max(res.data.total, 1) / Math.max(res.data.pageSize, 1)),
					});
				})
				.catch(err => console.error(err));
		}
	}, [contact, searchParams]);

	if (contact == null) {
		return null;
	}

	return (
		<div className="bg-base-grey-2" id="list">
			<div className="container min-h-96 py-24">
				<div className="flex flex-col lg:flex-row justify-between gap-4 mb-12">
					<div>
						<Typography variant="h4" asEl="h2" className="mb-4">
							Vagas disponíveis
						</Typography>
						<Typography variant="body-2" className="text-base-grey-4">
							Mostrando {data.pageSize} de {data.total}
						</Typography>
					</div>
					<div className="flex flex-col md:flex-row gap-4">
						<SelectField
							id="subject"
							label={'Filtrar por estado'}
							options={[
								{ value: '', label: 'Todos' },
								{ value: 'AC', label: 'Acre' },
								{ value: 'AL', label: 'Alagoas' },
								{ value: 'AP', label: 'Amapá' },
								{ value: 'AM', label: 'Amazonas' },
								{ value: 'BA', label: 'Bahia' },
								{ value: 'CE', label: 'Ceará' },
								{ value: 'DF', label: 'Distrito Federal' },
								{ value: 'ES', label: 'Espírito Santo' },
								{ value: 'GO', label: 'Goiás' },
								{ value: 'MA', label: 'Maranhão' },
								{ value: 'MT', label: 'Mato Grosso' },
								{ value: 'MS', label: 'Mato Grosso do Sul' },
								{ value: 'MG', label: 'Minas Gerais' },
								{ value: 'PA', label: 'Pará' },
								{ value: 'PB', label: 'Paraíba' },
								{ value: 'PR', label: 'Paraná' },
								{ value: 'PE', label: 'Pernambuco' },
								{ value: 'PI', label: 'Piauí' },
								{ value: 'RJ', label: 'Rio de Janeiro' },
								{ value: 'RN', label: 'Rio Grande do Norte' },
								{ value: 'RS', label: 'Rio Grande do Sul' },
								{ value: 'RO', label: 'Rondônia' },
								{ value: 'RR', label: 'Roraima' },
								{ value: 'SC', label: 'Santa Catarina' },
								{ value: 'SP', label: 'São Paulo' },
								{ value: 'SE', label: 'Sergipe' },
								{ value: 'TO', label: 'Tocantins' },
							]}
							value={searchParams.get('state')}
							setValue={value => setNewParam('state', value)}
						/>
						<SelectField
							id="subject"
							label={'Filtrar por modalidade'}
							options={[
								{ value: '', label: 'Todas' },
								{ value: 'Trabalho Remoto Permanente', label: 'Trabalho Remoto Permanente' },
								{ value: 'Presencial', label: 'Presencial' },
								{ value: 'Híbrido', label: 'Híbrido' },
							]}
							value={searchParams.get('workPolicy')}
							setValue={value => setNewParam('workPolicy', value)}
						/>
					</div>
				</div>

				<div className="flex flex-row justify-center md:justify-between gap-8 flex-wrap max-w-full mb-12">
					{data.jobs.map(job => (
						<OpportunityCard
							key={job.code}
							city={
								job.address
									? job.address?.city + ' - ' + job.address?.state
									: job.remoteWorkPolicy === 'Trabalho Remoto Permanente'
									? 'Remoto'
									: 'Não informado'
							}
							cod={job.code}
							edRequirement={job.education + ' ' + job.educationStatus}
							sector={job.sector}
							jobTitle={job.name}
							href={generateJobUrl(job)}
						/>
					))}
				</div>
				<Navigation
					page={Number(searchParams.get('page') ?? 1)}
					pageMax={data.pageMax}
					onPrevious={prevPage}
					onNext={nextPage}
				/>
			</div>
		</div>
	);
};

export default Jobs;
